import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DateInput } from '../components/ui/date-input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Receipt, Search, Eye, Edit, Trash2, Ban, Filter,
  ShoppingCart, Clock, CreditCard, X, Save, Loader2, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { formatUSD, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

const POSTransactionsPage = () => {
  const { user, currentOrg, canEdit } = useAuth();
  const { selectedFY } = useFiscalYear();

  // List state
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // View/Edit/Void state
  const [viewTx, setViewTx] = useState(null);
  const [editTx, setEditTx] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  const [editCustomerName, setEditCustomerName] = useState('');
  const [saving, setSaving] = useState(false);
  const [voidTx, setVoidTx] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  useEffect(() => {
    if (currentOrg) fetchTransactions(true);
  }, [currentOrg, selectedFY]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentOrg) fetchTransactions(true);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, filterStatus, dateFrom, dateTo]);

  const fetchTransactions = async (reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams({
        organization_id: currentOrg.id,
        skip: reset ? 0 : currentPage * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      if (searchTerm) params.append('search', searchTerm);
      if (dateFrom || (selectedFY && !dateFrom)) params.append('date_from', dateFrom || selectedFY?.start_date || '');
      if (dateTo || (selectedFY && !dateTo)) params.append('date_to', dateTo || selectedFY?.end_date || '');

      const [txRes, countRes] = await Promise.all([
        axios.get(`${API}/pos/transactions?${params}`),
        axios.get(`${API}/pos/transactions/count?organization_id=${currentOrg.id}${dateFrom ? `&date_from=${dateFrom}` : selectedFY ? `&date_from=${selectedFY.start_date}` : ''}${dateTo ? `&date_to=${dateTo}` : selectedFY ? `&date_to=${selectedFY.end_date}` : ''}`)
      ]);

      let data = txRes.data;
      // Apply client-side status filter
      if (filterStatus === 'active') data = data.filter(t => !t.is_voided);
      if (filterStatus === 'voided') data = data.filter(t => t.is_voided);

      if (reset) {
        setTransactions(data);
        setCurrentPage(1);
      } else {
        setTransactions(prev => [...prev, ...data]);
        setCurrentPage(prev => prev + 1);
      }
      setTotalCount(countRes.data?.count || 0);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const hasMore = transactions.length < totalCount;

  // ===== VIEW =====
  const openView = (tx) => setViewTx(tx);

  // ===== EDIT =====
  const openEdit = (tx) => {
    if (tx.is_voided) { toast.error('Cannot edit voided transaction'); return; }
    setEditTx(tx);
    setEditLines(tx.lines.map(l => ({
      ...l,
      quantity: l.quantity || l.qty || 0,
      unit_price: l.price || l.unit_price || 0,
      item_name: l.name || l.item_name || '',
      discount_percent: l.discount_percent || l.discount || 0,
    })));
    setEditNotes(tx.notes || '');
    setEditCustomerName(tx.customer_name || '');
  };

  const handleEditLineChange = (idx, field, value) => {
    const updated = [...editLines];
    updated[idx] = { ...updated[idx], [field]: value };
    // Recalc line total
    const qty = parseFloat(updated[idx].quantity) || 0;
    const price = parseFloat(updated[idx].unit_price) || 0;
    const disc = parseFloat(updated[idx].discount_percent) || 0;
    const sub = qty * price;
    updated[idx].line_total = sub - (sub * disc / 100);
    updated[idx].line_total_usd = updated[idx].line_total_usd != null
      ? (updated[idx].currency === 'EUR' && editTx?.exchange_rate > 1
        ? updated[idx].line_total / editTx.exchange_rate
        : updated[idx].line_total)
      : updated[idx].line_total;
    setEditLines(updated);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const lines = editLines.map(l => ({
        ...l,
        name: l.item_name || l.name,
        quantity: parseFloat(l.quantity) || 0,
        price: parseFloat(l.unit_price) || 0,
        unit_price: parseFloat(l.unit_price) || 0,
        discount_percent: parseFloat(l.discount_percent) || 0,
      }));
      await axios.put(`${API}/pos/transactions/${editTx.id}`, {
        lines,
        notes: editNotes,
        customer_name: editCustomerName,
      });
      toast.success('Transaction updated');
      setEditTx(null);
      fetchTransactions(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  // ===== VOID =====
  const openVoid = (tx) => {
    if (tx.is_voided) { toast.error('Already voided'); return; }
    setVoidTx(tx);
    setVoidReason('');
  };

  const handleVoid = async () => {
    setVoiding(true);
    try {
      await axios.put(`${API}/pos/invoices/${voidTx.id}/void?reason=${encodeURIComponent(voidReason || 'Voided by admin')}`);
      toast.success('Transaction voided');
      setVoidTx(null);
      fetchTransactions(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to void');
    }
    setVoiding(false);
  };

  // ===== DELETE =====
  const handleDelete = async (tx) => {
    if (!tx.is_voided) { toast.error('Void the transaction first before deleting'); return; }
    if (!window.confirm(`Permanently delete ${tx.receipt_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/pos/invoices/${tx.id}?restore_inventory=false`);
      toast.success('Transaction deleted');
      fetchTransactions(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // Helpers
  const fmtLBP = (v) => v ? Math.round(v).toLocaleString() : '0';

  if (!currentOrg) return <div className="p-8 text-center text-muted-foreground">Select an organization</div>;

  return (
    <div className="space-y-6" data-testid="pos-transactions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Receipt className="w-6 h-6 text-primary" />
            POS Transactions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">View, edit, and manage all POS sales transactions</p>
        </div>
        <Badge variant="outline" className="text-sm font-mono">
          {totalCount} transaction{totalCount !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by receipt # or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="pos-tx-search"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]" data-testid="pos-tx-filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="voided">Voided</SelectItem>
              </SelectContent>
            </Select>
            <DateInput value={dateFrom} onChange={setDateFrom} placeholder="From date" data-testid="pos-tx-date-from" />
            <DateInput value={dateTo} onChange={setDateTo} placeholder="To date" data-testid="pos-tx-date-to" />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Loading transactions...
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No POS transactions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]" data-testid="pos-transactions-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Receipt #</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Time</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Customer</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Payment</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Items</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Total (XOF)</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Total (EUR)</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr
                      key={tx.id}
                      className={`border-b hover:bg-muted/20 transition-colors ${tx.is_voided ? 'opacity-50' : ''}`}
                      data-testid={`pos-tx-row-${tx.id}`}
                    >
                      <td className={`p-3 font-mono text-sm ${tx.is_voided ? 'line-through' : ''}`}>
                        {tx.receipt_number}
                      </td>
                      <td className="p-3 text-sm">{formatDate(tx.date)}</td>
                      <td className="p-3 text-sm text-muted-foreground">{tx.time}</td>
                      <td className="p-3 text-sm">
                        <span className="font-medium">{tx.customer_name || 'Walk-in'}</span>
                        {tx.customer_code && <span className="text-muted-foreground ml-1 text-xs">{tx.customer_code}</span>}
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="text-[10px]">
                          {tx.payment_method || 'cash'}
                        </Badge>
                      </td>
                      <td className="p-3 text-right text-sm">{tx.lines?.length || 0}</td>
                      <td className={`p-3 text-right text-sm font-mono ${tx.is_voided ? 'line-through' : 'text-teal-500 font-medium'}`}>
                        {formatUSD(tx.total_usd)}
                      </td>
                      <td className={`p-3 text-right text-sm font-mono ${tx.is_voided ? 'line-through' : ''}`}>
                        {fmtLBP(tx.total_lbp)}
                      </td>
                      <td className="p-3 text-center">
                        {tx.is_voided ? (
                          <Badge variant="destructive" className="text-[10px]">VOIDED</Badge>
                        ) : (
                          <Badge className="bg-emerald-600 text-[10px]">Active</Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openView(tx)} data-testid={`view-tx-${tx.id}`} title="View">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {!tx.is_voided && canEdit() && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(tx)} data-testid={`edit-tx-${tx.id}`} title="Edit">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openVoid(tx)} data-testid={`void-tx-${tx.id}`} title="Void">
                                <Ban className="w-4 h-4 text-amber-500" />
                              </Button>
                            </>
                          )}
                          {tx.is_voided && user?.role === 'super_admin' && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(tx)} data-testid={`delete-tx-${tx.id}`} title="Delete">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {hasMore && (
            <div className="p-4 text-center border-t">
              <Button variant="outline" onClick={() => fetchTransactions(false)} disabled={loadingMore} data-testid="load-more-btn">
                {loadingMore ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                Load More ({transactions.length}/{totalCount})
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== VIEW DIALOG ===== */}
      <Dialog open={!!viewTx} onOpenChange={(v) => !v && setViewTx(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-tx-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-primary" />
              {viewTx?.receipt_number}
              {viewTx?.is_voided && <Badge variant="destructive">VOIDED</Badge>}
            </DialogTitle>
            <DialogDescription>Transaction details</DialogDescription>
          </DialogHeader>
          {viewTx && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="font-medium">{viewTx.date}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Time</p>
                  <p className="font-medium">{viewTx.time}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Customer</p>
                  <p className="font-medium">{viewTx.customer_name || 'Walk-in'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Payment</p>
                  <p className="font-medium capitalize">{viewTx.payment_method}</p>
                </div>
                {viewTx.cashier_name && (
                  <div>
                    <p className="text-xs text-muted-foreground">Cashier</p>
                    <p className="font-medium">{viewTx.cashier_name}</p>
                  </div>
                )}
                {viewTx.voucher_number && (
                  <div>
                    <p className="text-xs text-muted-foreground">Voucher</p>
                    <p className="font-mono text-xs">{viewTx.voucher_number}</p>
                  </div>
                )}
              </div>

              {/* Items table */}
              <div className="overflow-x-auto border rounded">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-2 text-xs">Item</th>
                      <th className="text-right p-2 text-xs w-16">Qty</th>
                      <th className="text-right p-2 text-xs w-24">Price</th>
                      <th className="text-right p-2 text-xs w-16">Disc%</th>
                      <th className="text-right p-2 text-xs w-24">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewTx.lines?.map((line, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-2 text-xs font-medium">{line.name || line.item_name}</td>
                        <td className="p-2 text-right text-xs">{line.quantity || line.qty}</td>
                        <td className="p-2 text-right text-xs font-mono">{formatUSD(line.price || line.unit_price)}</td>
                        <td className="p-2 text-right text-xs">{line.discount_percent || line.discount || 0}%</td>
                        <td className="p-2 text-right text-xs font-mono font-medium">{formatUSD(line.line_total_usd || line.line_total || line.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="space-y-1 text-sm w-64">
                  {viewTx.discount_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Discount</span>
                      <span className="text-red-400">-{formatUSD(viewTx.discount_amount)}</span>
                    </div>
                  )}
                  {viewTx.tax_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax</span>
                      <span>{formatUSD(viewTx.tax_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold border-t pt-1">
                    <span>Total (XOF)</span>
                    <span className="text-teal-500">{formatUSD(viewTx.total_usd)}</span>
                  </div>
                  {viewTx.total_lbp > 0 && (
                    <div className="flex justify-between font-bold">
                      <span>Total (EUR)</span>
                      <span>{fmtLBP(viewTx.total_lbp)}</span>
                    </div>
                  )}
                </div>
              </div>

              {viewTx.notes && (
                <div className="bg-muted/20 rounded p-3">
                  <p className="text-xs text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm">{viewTx.notes}</p>
                </div>
              )}

              {viewTx.is_voided && (
                <div className="bg-red-500/10 border border-red-500/20 rounded p-3">
                  <p className="text-xs text-red-400 font-semibold mb-1">Voided</p>
                  <p className="text-sm">Reason: {viewTx.void_reason || 'No reason provided'}</p>
                  <p className="text-xs text-muted-foreground mt-1">By: {viewTx.voided_by_name} at {viewTx.voided_at}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== EDIT DIALOG ===== */}
      <Dialog open={!!editTx} onOpenChange={(v) => !v && setEditTx(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-tx-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Edit {editTx?.receipt_number}
            </DialogTitle>
            <DialogDescription>Modify transaction line items</DialogDescription>
          </DialogHeader>
          {editTx && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Customer Name</Label>
                  <Input value={editCustomerName} onChange={e => setEditCustomerName(e.target.value)} data-testid="edit-customer-name" />
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={1} data-testid="edit-notes" />
                </div>
              </div>

              <div className="overflow-x-auto border rounded">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-2 text-xs">Item</th>
                      <th className="text-right p-2 text-xs w-20">Qty</th>
                      <th className="text-right p-2 text-xs w-28">Unit Price</th>
                      <th className="text-right p-2 text-xs w-20">Disc%</th>
                      <th className="text-right p-2 text-xs w-28">Line Total</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {editLines.map((line, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-2 text-xs font-medium">{line.item_name || line.name}</td>
                        <td className="p-2">
                          <Input type="number" className="h-7 text-xs text-right" value={line.quantity}
                            onChange={e => handleEditLineChange(i, 'quantity', e.target.value)}
                            min="0" data-testid={`edit-line-qty-${i}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input type="number" className="h-7 text-xs text-right" value={line.unit_price}
                            onChange={e => handleEditLineChange(i, 'unit_price', e.target.value)}
                            min="0" step="0.01" data-testid={`edit-line-price-${i}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input type="number" className="h-7 text-xs text-right" value={line.discount_percent}
                            onChange={e => handleEditLineChange(i, 'discount_percent', e.target.value)}
                            min="0" max="100" data-testid={`edit-line-disc-${i}`}
                          />
                        </td>
                        <td className="p-2 text-right text-xs font-mono font-medium">
                          {formatUSD(line.line_total || 0)}
                        </td>
                        <td className="p-1 text-center">
                          {editLines.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => setEditLines(prev => prev.filter((_, idx) => idx !== i))}
                              data-testid={`edit-line-delete-${i}`}
                              title="Remove item"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setEditTx(null)}>
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== VOID DIALOG ===== */}
      <Dialog open={!!voidTx} onOpenChange={(v) => !v && setVoidTx(null)}>
        <DialogContent className="max-w-md" data-testid="void-tx-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <Ban className="w-5 h-5" />
              Void Transaction
            </DialogTitle>
            <DialogDescription>
              This will reverse inventory and accounting entries for {voidTx?.receipt_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded p-3 text-sm">
              <p><strong>{voidTx?.receipt_number}</strong> — {formatUSD(voidTx?.total_usd)} XOF</p>
              <p className="text-xs text-muted-foreground mt-1">{voidTx?.lines?.length} items • {voidTx?.customer_name || 'Walk-in'}</p>
            </div>
            <div>
              <Label className="text-xs">Reason for voiding</Label>
              <Textarea
                value={voidReason}
                onChange={e => setVoidReason(e.target.value)}
                placeholder="Enter reason..."
                rows={2}
                data-testid="void-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setVoidTx(null)}>Cancel</Button>
              <Button variant="destructive" onClick={handleVoid} disabled={voiding} data-testid="confirm-void-btn">
                {voiding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                Void Transaction
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSTransactionsPage;
